import { useMemo } from 'react';

import {
  AppIcon,
  LifeReadinessCategory,
  LifeReadinessQuestion,
  LifeReadinessQuestionUserLevel,
  LifeReadinessSubcategory,
  QuestionView,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, Button, type SxProps, type Theme, Typography } from '@mui/material';

import { useAgentContext } from './CategoryAnswersView.agentContext';
import {
  getQuestionsForSubcategory,
  getSubcategoriesForCategory,
  getUserLevelMap,
} from './utils/lifeReadinessQuestionnaire.util';

const LIFE_READINESS_STRINGS = appStrings.modules.lifeReadiness;
const QUESTIONNAIRE_STRINGS = LIFE_READINESS_STRINGS.questionTooltips.consolidated;

type CategoryAnswersViewProps = Readonly<{
  category: LifeReadinessCategory;
  subcategories: LifeReadinessSubcategory[];
  questions: LifeReadinessQuestion[];
  userLevels: LifeReadinessQuestionUserLevel[];
  onBack: () => void;
}>;

/** Someone else's answers for one category, every subcategory at once, with no way to change them. */
export const CategoryAnswersView = ({
  category,
  subcategories,
  questions,
  userLevels,
  onBack,
}: CategoryAnswersViewProps) => {
  const userLevelMap = useMemo(() => getUserLevelMap(userLevels), [userLevels]);

  const subcategoriesForCategory = getSubcategoriesForCategory(subcategories, category.id);

  const hasAnyAnswer = subcategoriesForCategory.some((subcategory) =>
    questions.some(
      (question) =>
        question.life_survey_subcategory_id === subcategory.id &&
        userLevelMap[question.id] !== undefined,
    ),
  );

  useAgentContext({ category, subcategoriesForCategory, questions, userLevelMap });

  return (
    <Box sx={containerSx}>
      <Button
        id="life-readiness-answers-back-button"
        hierarchy="Tertiary color"
        size="md"
        onClick={onBack}
        startIcon={<AppIcon iconName="ArrowLeft" diameter="20px" color="currentColor" />}
        sx={backButtonSx}
      >
        {LIFE_READINESS_STRINGS.backToCategories}
      </Button>

      {!hasAnyAnswer ? (
        <Typography variant="text-md-regular">
          {LIFE_READINESS_STRINGS.noAnswersForCategory}
        </Typography>
      ) : (
        subcategoriesForCategory.map((subcategory) => (
          <QuestionView
            key={subcategory.id}
            header={{
              title: category.name,
              description: category.description,
              subCategory: subcategory.name,
              tooltips: Object.values(QUESTIONNAIRE_STRINGS),
              showChip: subcategoriesForCategory.length > 1,
            }}
            questions={getQuestionsForSubcategory(questions, subcategory.id, userLevelMap)}
          />
        ))
      )}
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  width: '100%',
  maxWidth: '1098px',
});

const backButtonSx: SxProps<Theme> = {
  alignSelf: 'flex-start',
};
